import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  shift,
  useDismiss,
  useFloating,
  useInteractions,
} from "@floating-ui/react";
import { Input, Spinner } from "@visiblethread/react-components";
import clsx from "clsx";
import Flex from "components/Flex";
import UserAvatar from "components/UserAvatar";
import { idPropType } from "constants/app";
import { ENTITY_TYPE_PATH } from "constants/entityTypes";
import { Check, SearchIcon } from "lucide-react";
import {
  arrayOf,
  func,
  number,
  object,
  oneOf,
  oneOfType,
  shape,
  string,
} from "prop-types";
import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  addAssignee,
  fetchAssignableUsers,
  removeAssignee,
  selectAssignableUsers,
  selectIsAssignableUsersLoading,
} from "slices/assignees";
import { getFullName } from "utils/getFullName";

import classes from "./AssigneePicker.module.scss";
import { compareByAssignedAt, matchesSearch } from "./helpers";

const AssigneePicker = ({
  entityType,
  entityId,
  assignees = [],
  triggerRef,
  onClose,
  onAssigneesChange,
  onCommitted,
  onBeforeCommit,
}) => {
  const dispatch = useDispatch();

  const assignableUsers = useSelector(selectAssignableUsers);
  const isLoading = useSelector(selectIsAssignableUsersLoading);

  const [searchTerm, setSearchTerm] = useState("");

  const { refs, floatingStyles, context } = useFloating({
    open: true,
    onOpenChange: (open) => {
      if (!open) {
        onClose();
      }
    },
    placement: "bottom-start",
    whileElementsMounted: (referenceEl, floatingEl, update) =>
      autoUpdate(referenceEl, floatingEl, update, { elementResize: false }),
    middleware: [offset(4), flip(), shift({ padding: 8 })],
  });
  const dismiss = useDismiss(context);

  const { getFloatingProps } = useInteractions([dismiss]);

  useLayoutEffect(() => {
    refs.setReference(triggerRef.current);
  }, [refs, triggerRef]);

  useEffect(() => {
    dispatch(fetchAssignableUsers());
  }, []);

  const assignedUserIds = useMemo(
    () => new Set(assignees.map((assignee) => assignee.workspaceUserId)),
    [assignees]
  );

  const assignedAtByUserId = useMemo(
    () =>
      new Map(
        assignees.map((assignee) => [
          assignee.workspaceUserId,
          assignee.assignedAt,
        ])
      ),
    [assignees]
  );

  const filteredUsers = useMemo(
    () => assignableUsers.filter((user) => matchesSearch(user, searchTerm)),
    [assignableUsers, searchTerm]
  );

  const assignedFilteredUsers = useMemo(
    () =>
      filteredUsers
        .filter((user) => assignedUserIds.has(user.workspaceUserId))
        .map((user) => ({
          ...user,
          assignedAt: assignedAtByUserId.get(user.workspaceUserId),
        }))
        .sort(compareByAssignedAt),
    [filteredUsers, assignedUserIds, assignedAtByUserId]
  );

  const unassignedFilteredUsers = useMemo(
    () =>
      filteredUsers.filter(
        (user) => !assignedUserIds.has(user.workspaceUserId)
      ),
    [filteredUsers, assignedUserIds]
  );

  const handleToggleUser = async (user) => {
    const isAssigned = assignedUserIds.has(user.workspaceUserId);
    const isAssigning = !isAssigned;
    const previousAssignees = assignees;

    let cascade = false;

    if (onBeforeCommit) {
      const decision = await onBeforeCommit(user, isAssigning, onClose);

      if (!decision?.proceed) {
        return;
      }
      cascade = Boolean(decision.cascade);
    }

    const cascadeInfo = cascade ? { user, isAssigning } : undefined;

    if (onAssigneesChange) {
      onAssigneesChange(
        isAssigned
          ? assignees.filter(
              (assignee) => assignee.workspaceUserId !== user.workspaceUserId
            )
          : [...assignees, { ...user, assignedAt: new Date().toISOString() }],
        cascadeInfo
      );
    }

    try {
      const confirmedAssignees = isAssigned
        ? await dispatch(
            removeAssignee(entityType, entityId, user.workspaceUserId, cascade)
          )
        : await dispatch(
            addAssignee(entityType, entityId, user.workspaceUserId, cascade)
          );

      if (onAssigneesChange) {
        onAssigneesChange(confirmedAssignees, cascadeInfo);
      }

      if (onCommitted) {
        onCommitted(confirmedAssignees);
      }
    } catch {
      if (onAssigneesChange) {
        onAssigneesChange(
          previousAssignees,
          cascade ? { user, isAssigning: !isAssigning } : undefined
        );
      }
    }
  };

  const renderRow = (user) => {
    const isAssigned = assignedUserIds.has(user.workspaceUserId);
    return (
      <button
        key={user.workspaceUserId}
        type="button"
        className={classes.assignee_picker__row}
        onClick={() => handleToggleUser(user)}
        data-testid={`assignee-picker-row-${user.workspaceUserId}`}
      >
        <UserAvatar user={user} />
        <span className={classes.assignee_picker__row__name}>
          {getFullName(user)}
        </span>
        {isAssigned && (
          <Check
            size={16}
            className={classes.assignee_picker__row__check}
            data-testid={`assignee-picker-check-${user.workspaceUserId}`}
          />
        )}
      </button>
    );
  };

  return (
    <FloatingPortal>
      <div
        ref={refs.setFloating}
        style={floatingStyles}
        className={classes.assignee_picker}
        data-testid="assignee-picker"
        {...getFloatingProps()}
      >
        <div className={classes.assignee_picker__search}>
          <Input
            className={classes.assignee_picker__search__input}
            data-testid="assignee-picker-search"
            onChange={setSearchTerm}
            placeholder="Search Users"
            prefix={<SearchIcon size={16} />}
            value={searchTerm}
            autoFocus
          />
        </div>

        <div className={classes.assignee_picker__list}>
          {isLoading && (
            <Flex
              justifyContent="center"
              className={classes.assignee_picker__state}
            >
              <Spinner size={20} />
            </Flex>
          )}

          {!isLoading && filteredUsers.length === 0 && (
            <Flex
              justifyContent="center"
              className={clsx(
                classes.assignee_picker__state,
                classes.assignee_picker__empty
              )}
            >
              No Users Found
            </Flex>
          )}

          {!isLoading && assignedFilteredUsers.map(renderRow)}

          {!isLoading &&
            assignedFilteredUsers.length > 0 &&
            unassignedFilteredUsers.length > 0 && (
              <hr className={classes.assignee_picker__divider} />
            )}

          {!isLoading && unassignedFilteredUsers.map(renderRow)}
        </div>
      </div>
    </FloatingPortal>
  );
};

const assigneeShape = shape({
  workspaceUserId: idPropType.isRequired,
  userName: string,
  firstName: string,
  lastName: string,
  assignedAt: string,
});

AssigneePicker.propTypes = {
  entityType: oneOf(Object.keys(ENTITY_TYPE_PATH)).isRequired,
  entityId: oneOfType([number, string]).isRequired,
  assignees: arrayOf(assigneeShape),
  triggerRef: object.isRequired,
  onClose: func.isRequired,
  onAssigneesChange: func,
  onCommitted: func,
  onBeforeCommit: func,
};

export default AssigneePicker;
